import type { StorefrontProduct } from '../api/client';
import { orderable } from './variantStock';

export type PendingLine = { slug: string; variantId: string; quantity: number };

// Persist only identifiers and quantity; prices and inventory are always read again from the store.
export function readPendingLine(raw: string | null): PendingLine | null {
  try {
    const row = JSON.parse(raw ?? 'null');
    if (!row || typeof row.slug !== 'string' || typeof row.variantId !== 'string') return null;
    if (!Number.isInteger(row.quantity) || row.quantity < 1 || row.quantity > 99) return null;
    return { slug: row.slug, variantId: row.variantId, quantity: row.quantity };
  } catch { return null; }
}

// An unavailable variant cannot enter checkout, including a theme sample.
export function orderableVariant(product: StorefrontProduct, line: PendingLine) {
  if (product.status !== 'ACTIVE' || product.slug !== line.slug || product.id.startsWith('sample-')) return null;
  const variant = product.variants.find((item) => item.id === line.variantId);
  return Number.isInteger(line.quantity) && line.quantity >= 1 && line.quantity <= 99 && variant && Number(variant.price) >= 0 && orderable(variant, line.quantity) && Number.isFinite(Number(variant.price)) ? variant : null;
}

// Accept the previous single-line format, but persist only identifiers and quantities.
export function readPendingLines(raw: string | null): PendingLine[] {
  try {
    const stored = JSON.parse(raw ?? 'null');
    const rows = Array.isArray(stored) ? stored : stored ? [stored] : [];
    if (rows.length > 50) return [];
    const parsed = rows.map((row) => readPendingLine(JSON.stringify(row)));
    if (parsed.some((row) => !row)) return [];
    const lines = parsed as PendingLine[];
    return new Set(lines.map((line) => line.variantId)).size === lines.length ? lines : [];
  } catch { return []; }
}

// Adds lines to the cart, summing a variant it already holds; null when a line conflicts or a limit is passed.
export function mergePendingLines(current: PendingLine[], incoming: PendingLine[]): PendingLine[] | null {
  const next = current.map((line) => ({ ...line }));
  for (const line of incoming) {
    const existing = next.find((row) => row.variantId === line.variantId);
    if (existing && existing.slug !== line.slug) return null;
    if (existing) existing.quantity += line.quantity;
    else next.push({ ...line });
  }
  return next.length <= 50 && next.every((line) => Number.isInteger(line.quantity) && line.quantity > 0 && line.quantity <= 99) ? next : null;
}

// Sets one line's quantity, kept between 1 and 99; a line the cart does not hold leaves the cart as it was.
export function setLineQuantity(current: PendingLine[], variantId: string, quantity: number): PendingLine[] {
  const wanted = Math.max(1, Math.min(99, Math.trunc(quantity) || 1));
  if (!current.some((line) => line.variantId === variantId && line.quantity !== wanted)) return current;
  return current.map((line) => (line.variantId === variantId ? { ...line, quantity: wanted } : line));
}
